import { Children, Fragment, type ReactNode } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { AppText } from "./AppText";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

/**
 * A vertical list of `ListRow`s separated by hairline dividers, framed by a
 * single card. Replaces the previous pattern of one bordered box per row.
 */
export function List({
  children,
  style,
}: {
  children: ReactNode;
  style?: object;
}) {
  const { stylesheet } = useThemedStyles(makeStyles);
  const items = Children.toArray(children).filter(Boolean);
  return (
    <View style={[stylesheet.list, style]}>
      {items.map((child, index: number) => (
        <Fragment key={index}>
          {index > 0 ? <View style={stylesheet.divider} /> : null}
          {child}
        </Fragment>
      ))}
    </View>
  );
}

export function ListRow({
  title,
  description,
  meta,
  trailing,
  onPress,
  accessibilityLabel,
}: {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  onPress?: () => void;
  accessibilityLabel?: string;
}) {
  const { stylesheet } = useThemedStyles(makeStyles);
  const body = (
    <>
      <View style={stylesheet.main}>
        {typeof title === "string" ? (
          <AppText weight="semibold">{title}</AppText>
        ) : (
          title
        )}
        {description ? (
          typeof description === "string" ? (
            <AppText size="xs" tone="muted">
              {description}
            </AppText>
          ) : (
            description
          )
        ) : null}
        {meta ? <View style={stylesheet.meta}>{meta}</View> : null}
      </View>
      {trailing ? <View style={stylesheet.trailing}>{trailing}</View> : null}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          accessibilityLabel ?? (typeof title === "string" ? title : undefined)
        }
        onPress={onPress}
        style={({ hovered, pressed }) => [
          stylesheet.row,
          hovered || pressed ? stylesheet.rowHover : null,
        ]}
      >
        {body}
      </Pressable>
    );
  }

  return <View style={stylesheet.row}>{body}</View>;
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    list: {
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.card,
      backgroundColor: colors.background,
      overflow: "hidden",
    },
    divider: {
      height: 1,
      backgroundColor: colors.hairline,
    },
    row: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      paddingVertical: spacing.m,
      paddingHorizontal: spacing.l,
      backgroundColor: colors.background,
    },
    rowHover: { backgroundColor: colors.surfaceSunken },
    main: { flex: 1, minWidth: 0, gap: 2 },
    meta: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      marginTop: spacing.xs,
    },
    trailing: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: spacing.s,
      justifyContent: "flex-end",
    },
  });
  return { stylesheet };
};
